import { useState, useEffect } from 'react'
import {
    Eye,
    Save,
    Loader2,
    Check,
    AlertCircle,
    Info,
    Clock,
    Activity,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { trpc } from '@/lib/trpc'
import type { VisibilitySettings } from '@/types'
import { VisibilityKeywordsCitiesSettings } from '@/components/workspace/VisibilityKeywordsCitiesSettings'

const PLATFORMS = [
    { value: 'blinkit', label: 'Blinkit' },
    { value: 'bbnow', label: 'BigBasket Now' },
]
const SUPPORTED_PLATFORM_VALUES = new Set(PLATFORMS.map((platform) => platform.value))

export function VisibilitySettingsPage() {
    const { currentWorkspace } = useWorkspace()
    const workspaceId = currentWorkspace?.id

    const [settings, setSettings] = useState<VisibilitySettings | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle')
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const [isEnabled, setIsEnabled] = useState(false)
    const [scrapesPerDay, setScrapesPerDay] = useState(4)
    const [pinsPerCluster, setPinsPerCluster] = useState(5)
    const [darkstoresPerPin, setDarkstoresPerPin] = useState(1)
    const [enabledPlatforms, setEnabledPlatforms] = useState<string[]>(['blinkit'])
    const [scrapeStartHour, setScrapeStartHour] = useState(9)
    const [scrapeEndHour, setScrapeEndHour] = useState(21)
    const [targetBrand, setTargetBrand] = useState('')
    const effectiveStoresPerCity = pinsPerCluster * darkstoresPerPin

    const utils = trpc.useUtils()
    const updateSettingsMutation = trpc.visibility.updateSettings.useMutation()

    useEffect(() => {
        if (!workspaceId) return

        const loadData = async () => {
            try {
                setIsLoading(true)
                const settingsData = await utils.visibility.settings.fetch({ workspaceId })

                if (settingsData) {
                    setSettings(settingsData)
                    setIsEnabled(settingsData.isEnabled)
                    setScrapesPerDay(settingsData.scrapesPerDay)
                    setPinsPerCluster(Math.max(1, Math.min(50, settingsData.pinsPerCluster || 5)))
                    setDarkstoresPerPin(Math.max(1, Math.min(20, settingsData.darkstoresPerPin || 1)))
                    const supportedEnabledPlatforms = settingsData.enabledPlatforms.filter((platform) =>
                        SUPPORTED_PLATFORM_VALUES.has(platform)
                    )
                    setEnabledPlatforms(supportedEnabledPlatforms.length > 0 ? supportedEnabledPlatforms : ['blinkit'])
                    setScrapeStartHour(settingsData.scrapeStartHour)
                    setScrapeEndHour(settingsData.scrapeEndHour)
                    setTargetBrand(settingsData.targetBrand || '')
                }
            } catch (error) {
                console.error('Error loading settings:', error)
                setErrorMessage('Failed to load settings')
            } finally {
                setIsLoading(false)
            }
        }

        loadData()
    }, [workspaceId])

    const handleSave = async () => {
        if (!workspaceId) return

        if (scrapeStartHour >= scrapeEndHour) {
            setErrorMessage('Start hour must be less than end hour')
            setSaveStatus('error')
            setTimeout(() => setSaveStatus('idle'), 3000)
            return
        }
        if (effectiveStoresPerCity > 50) {
            setErrorMessage('PINs per Cluster × Darkstores per PIN cannot exceed 50')
            setSaveStatus('error')
            setTimeout(() => setSaveStatus('idle'), 3000)
            return
        }

        try {
            setIsSaving(true)
            setErrorMessage(null)

            await updateSettingsMutation.mutateAsync({
                workspaceId,
                isEnabled,
                scrapesPerDay,
                pinsPerCluster,
                darkstoresPerPin,
                storesPerCity: effectiveStoresPerCity,
                enabledPlatforms: enabledPlatforms as ('blinkit' | 'bbnow')[],
                scrapeStartHour,
                scrapeEndHour,
                targetBrand: targetBrand.trim() || null,
            })

            setSaveStatus('success')
            setTimeout(() => setSaveStatus('idle'), 3000)
        } catch (error) {
            console.error('Error saving settings:', error)
            setErrorMessage(error instanceof Error ? error.message : 'Failed to save settings')
            setSaveStatus('error')
            setTimeout(() => setSaveStatus('idle'), 3000)
        } finally {
            setIsSaving(false)
        }
    }

    const togglePlatform = (platform: string) => {
        setEnabledPlatforms((prev) => {
            if (prev.includes(platform)) {
                if (prev.length === 1) return prev
                return prev.filter((p) => p !== platform)
            }
            return [...prev, platform]
        })
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        )
    }

    return (
        <div className="p-6 md:p-8 w-full animate-fade-in">
            <div className="max-w-[1000px] mx-auto">
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <Eye className="h-8 w-8 text-primary" />
                        <h1 className="app-page-title">Visibility Settings</h1>
                    </div>
                    <p className="app-page-subtitle">
                        Configure automated scraping for Share of Voice (SOV) tracking across platforms.
                    </p>
                </div>

                <Card className="mb-6 border-primary/20 bg-primary/5">
                    <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                            <Info className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-sm font-medium text-foreground">About Visibility Scraping</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                    The visibility module automatically scrapes search results from Quick Commerce
                                    platforms to track your products' Share of Voice (SOV). Configure the frequency
                                    and platforms below.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="mb-6">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle className="text-lg">Scraping Status</CardTitle>
                                <CardDescription>
                                    Enable or disable automated visibility scraping
                                </CardDescription>
                            </div>
                            <div className="flex items-center gap-3">
                                <span
                                    className={`text-sm font-medium ${isEnabled ? 'text-green-600' : 'text-muted-foreground'}`}
                                >
                                    {isEnabled ? 'Enabled' : 'Disabled'}
                                </span>
                                <Switch checked={isEnabled} onCheckedChange={setIsEnabled} />
                            </div>
                        </div>
                    </CardHeader>
                </Card>

                <Card className="mb-6">
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <Activity className="h-5 w-5 text-primary" />
                            <div>
                                <CardTitle className="text-lg">Scraping Configuration</CardTitle>
                                <CardDescription>
                                    Configure scraping density using the same hierarchy as other modules
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="scrapes-per-day">Scrapes Per Day</Label>
                            <p className="text-sm text-muted-foreground">Number of times to scrape per day (1-24)</p>
                            <Input
                                id="scrapes-per-day"
                                type="number"
                                min={1}
                                max={24}
                                value={scrapesPerDay}
                                onChange={(e) => setScrapesPerDay(Number(e.target.value))}
                                className="max-w-xs"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="pins-per-cluster">PINs per Cluster</Label>
                            <p className="text-sm text-muted-foreground">
                                Number of PIN codes to sample in each cluster (1-50)
                            </p>
                            <Input
                                id="pins-per-cluster"
                                type="number"
                                min={1}
                                max={50}
                                value={pinsPerCluster}
                                onChange={(e) =>
                                    setPinsPerCluster(Math.max(1, Math.min(50, Number(e.target.value) || 1)))
                                }
                                className="max-w-xs"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="darkstores-per-pin">Darkstores per PIN</Label>
                            <p className="text-sm text-muted-foreground">
                                Number of darkstores to sample in each PIN code (1-20)
                            </p>
                            <Input
                                id="darkstores-per-pin"
                                type="number"
                                min={1}
                                max={20}
                                value={darkstoresPerPin}
                                onChange={(e) =>
                                    setDarkstoresPerPin(Math.max(1, Math.min(20, Number(e.target.value) || 1)))
                                }
                                className="max-w-xs"
                            />
                        </div>

                        <div className="rounded-lg border bg-muted/20 p-3">
                            <p className="text-sm text-muted-foreground">
                                Effective stores per city:{' '}
                                <span className="font-medium text-foreground">{effectiveStoresPerCity}</span>
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                                Saved as a single sampling cap in visibility settings.
                            </p>
                        </div>

                        <div className="space-y-2 pt-4 border-t border-border">
                            <Label htmlFor="target-brand">Target Brand Name</Label>
                            <p className="text-sm text-muted-foreground">
                                The exact brand name to match when calculating Share of Voice. If left blank, it will automatically use the brand of the first product added to your watchlist.
                            </p>
                            <Input
                                id="target-brand"
                                type="text"
                                placeholder="e.g. Amul, Nandini"
                                value={targetBrand}
                                onChange={(e) => setTargetBrand(e.target.value)}
                                className="max-w-md"
                            />
                        </div>
                    </CardContent>
                </Card>

                <Card className="mb-6">
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <Clock className="h-5 w-5 text-primary" />
                            <div>
                                <CardTitle className="text-lg">Scrape Hours</CardTitle>
                                <CardDescription>
                                    Set the time window for scraping (24-hour format)
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <Label htmlFor="start-hour">Start Hour</Label>
                                <Select
                                    value={scrapeStartHour.toString()}
                                    onValueChange={(value) => setScrapeStartHour(Number(value))}
                                >
                                    <SelectTrigger id="start-hour">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Array.from({ length: 24 }, (_, i) => (
                                            <SelectItem key={i} value={i.toString()}>
                                                {i.toString().padStart(2, '0')}:00
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="end-hour">End Hour</Label>
                                <Select
                                    value={scrapeEndHour.toString()}
                                    onValueChange={(value) => setScrapeEndHour(Number(value))}
                                >
                                    <SelectTrigger id="end-hour">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Array.from({ length: 24 }, (_, i) => (
                                            <SelectItem key={i} value={i.toString()}>
                                                {i.toString().padStart(2, '0')}:00
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle className="text-lg">Enabled Platforms</CardTitle>
                        <CardDescription>Select platforms to scrape (at least one required)</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {PLATFORMS.map((platform) => (
                                <div
                                    key={platform.value}
                                    className="flex items-center justify-between p-4 border rounded-lg"
                                >
                                    <span className="font-medium">{platform.label}</span>
                                    <Switch
                                        checked={enabledPlatforms.includes(platform.value)}
                                        onCheckedChange={() => togglePlatform(platform.value)}
                                    />
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {workspaceId && (
                    <div className="mb-6">
                        <VisibilityKeywordsCitiesSettings workspaceId={workspaceId} />
                    </div>
                )}

                {errorMessage && (
                    <Card className="mb-6 border-destructive bg-destructive/5">
                        <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                                <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium text-destructive">{errorMessage}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                <div className="flex justify-end gap-3">
                    <Button onClick={handleSave} disabled={isSaving} size="lg">
                        {isSaving ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Saving...
                            </>
                        ) : saveStatus === 'success' ? (
                            <>
                                <Check className="h-4 w-4 mr-2" />
                                Saved!
                            </>
                        ) : (
                            <>
                                <Save className="h-4 w-4 mr-2" />
                                Save Settings
                            </>
                        )}
                    </Button>
                </div>

                {settings?.lastScrapeAt && (
                    <div className="mt-6 p-4 bg-muted/30 rounded-lg">
                        <p className="text-sm text-muted-foreground">
                            Last scraped:{' '}
                            <span className="font-medium text-foreground">
                                {new Date(settings.lastScrapeAt).toLocaleString()}
                            </span>
                        </p>
                        {settings.nextScrapeAt && (
                            <p className="text-sm text-muted-foreground mt-1">
                                Next scrape:{' '}
                                <span className="font-medium text-foreground">
                                    {new Date(settings.nextScrapeAt).toLocaleString()}
                                </span>
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
